import { Element, CombinationResult } from '../types';
import { getAuthHeaders } from './authService';

// URL сервера API
const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Получить все элементы из API
 */
export const getAllElements = async (): Promise<Element[]> => {
  try {
    const response = await fetch(`${API_URL}/elements`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    });
    
    console.log('Полученные элементы:', response);

    if (!response.ok) {
      throw new Error(`Ошибка получения элементов: ${response.statusText}`);
    }
    const data = await response.json();
    return data.success ? data.data : [];
  } catch (error) {
    console.error('Ошибка при получении элементов:', error);
    return [];
  }
};

/**
 * Получить базовые элементы из API
 */
export const getBasicElements = async (): Promise<Element[]> => {
  try {
    const response = await fetch(`${API_URL}/basic`, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    });
    
    if (!response.ok) {
      throw new Error(`Ошибка получения базовых элементов: ${response.statusText}`);
    }
    const data = await response.json();
    console.log('Полученные базовые элементы:', data);

    return data.success ? data.result : [];
  } catch (error) {
    console.error('Ошибка при получении базовых элементов:', error);
    return [];
  }
};

/**
 * Проверить комбинацию элементов и получить результат
 */
export const checkCombination = async (element1Id: string, element2Id: string): Promise<CombinationResult | null> => {
  try {
    const response = await fetch(`${API_URL}/combine`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({ element1Id, element2Id }),
    });
    
    if (response.status === 404) {
      return null; // Комбинация не найдена
    }
    
    if (!response.ok) {
      throw new Error(`Ошибка проверки комбинации: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Полученные данные:', data);
    
    if (data.success) {
      return {
        newElement: data.result,
        isNew: data.isNew
      };
    }
    
    return null;
  } catch (error) {
    console.error('Ошибка при проверке комбинации:', error);
    return null;
  }
};

/**
 * Создать новый элемент через API с помощью ИИ
 */
export const createNewElement = async (element1: Element, element2: Element, existingElements: Element[]): Promise<CombinationResult | null> => {
  try {
    console.log(`Отправляем запрос на создание элемента: ${element1.name} + ${element2.name}`);
    
    const response = await fetch(`${API_URL}/elements/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        element1,
        element2,
        existingElements
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Ошибка создания элемента: ${errorData.message || response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data.success && data.data && data.data.element) {
      console.log(`Получен ответ: ${data.data.isNew ? 'Новый' : 'Существующий'} элемент ${data.data.element.name}`);
      return {
        newElement: data.data.element,
        isNew: data.data.isNew
      };
    }
    
    throw new Error(data.message || 'Неизвестная ошибка');
  } catch (error) {
    console.error('Ошибка при создании элемента:', error);
    return null;
  }
};

/**
 * Создать или получить существующую комбинацию элементов
 * Эта функция проверяет существующую комбинацию, а если её нет - создает новый элемент через ИИ автоматически
 */
export const createOrGetCombination = async (element1: Element, element2: Element, existingElements: Element[]): Promise<CombinationResult | null> => {
  try {
    console.log(`Поиск или создание комбинации: ${element1.name} + ${element2.name}`);
    
    // Сначала проверяем, существует ли комбинация
    const result = await checkCombination(element1._id, element2._id);
    
    if (result) {
      console.log(`Найдена существующая комбинация: ${result.newElement.name}`);
      return result;
    }
    
    // Если комбинация не найдена, создаем новый элемент через ИИ
    console.log('Комбинация не найдена, создаем новый элемент через ИИ');
    
    // Вызываем функцию создания через ИИ
    const newElementResult = await createNewElement(element1, element2, existingElements);
    
    if (newElementResult) {
      console.log(`Создан новый элемент: ${newElementResult.newElement.name}`);
      return newElementResult;
    } else {
      console.error('Не удалось создать новый элемент через ИИ');
      return null;
    }
  } catch (error) {
    console.error('Ошибка при создании или получении комбинации:', error);
    return null;
  }
}; 